if (!customElements.get('cart-variant-selector')) {
  let cartVariantUpdateInProgress = false;

  customElements.define(
    'cart-variant-selector',
    class CartVariantSelector extends HTMLElement {
      constructor() {
        super();
        this.isUpdating = false;
        this.selects = Array.from(this.querySelectorAll('[data-option-position]'));
        this.status = this.querySelector('.cart-variant-selector__status');
        this.data = JSON.parse(this.querySelector('[data-cart-variant-data]')?.textContent || '{}');
        this.variants = this.data.variants || [];
        this.previousValues = this.selectedValues();
        this.onChange = this.onChange.bind(this);
      }

      connectedCallback() {
        if (this.hasAttribute('data-change-listener-bound')) return;
        this.setAttribute('data-change-listener-bound', 'true');
        this.selects.forEach((select) => select.addEventListener('change', this.onChange));
        this.updateOptionAvailability();
        if (cartVariantUpdateInProgress) this.setBusy(true);
      }

      disconnectedCallback() {
        this.selects.forEach((select) => select.removeEventListener('change', this.onChange));
        this.removeAttribute('data-change-listener-bound');
      }

      selectedValues() {
        return this.selects.map((select) => select.value);
      }

      findVariant(values) {
        return this.variants.find((variant) =>
          values.every((value, index) => variant.options[index] === value),
        );
      }

      resolveVariant(changedSelect, values) {
        const exactVariant = this.findVariant(values);
        if (exactVariant?.available) return exactVariant;

        const changedIndex = this.selects.indexOf(changedSelect);
        if (changedIndex < 0) return exactVariant;
        return this.variants.find(
          (variant) => variant.available && variant.options[changedIndex] === changedSelect.value,
        ) || exactVariant;
      }

      syncSelections(variant) {
        if (!variant) return;
        this.selects.forEach((select, index) => {
          select.value = variant.options[index];
        });
      }

      updateOptionAvailability() {
        this.selects.forEach((select, optionIndex) => {
          Array.from(select.options).forEach((option) => {
            const matchingVariants = this.variants.filter(
              (variant) => variant.options[optionIndex] === option.value,
            );
            const exists = matchingVariants.length > 0;
            const available = matchingVariants.some((variant) => variant.available);
            option.disabled = !exists || !available;
            option.dataset.state = !exists ? 'unavailable' : available ? 'available' : 'sold-out';
          });
        });
      }

      setBusy(busy) {
        this.isUpdating = busy;
        this.toggleAttribute('aria-busy', busy);
        this.selects.forEach((select) => (select.disabled = busy));
        this.closest('.wt-cart__item')?.classList.toggle('cart-variant-updating', busy);
      }

      setStatus(message, isError = false) {
        if (!this.status) return;
        this.status.textContent = message;
        this.status.classList.toggle('cart-variant-selector__status--error', isError);
      }

      restoreSelections() {
        this.selects.forEach((select, index) => {
          select.value = this.previousValues[index];
        });
        this.updateOptionAvailability();
      }

      normalizedProperties(properties = {}) {
        return Object.fromEntries(
          Object.entries(properties)
            .filter(([, value]) => value !== null && value !== '')
            .sort(([left], [right]) => left.localeCompare(right)),
        );
      }

      sameProperties(left = {}, right = {}) {
        return JSON.stringify(this.normalizedProperties(left)) === JSON.stringify(this.normalizedProperties(right));
      }

      async fetchCart() {
        const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error('Unable to read the cart.');
        return response.json();
      }

      targetLine(cart, variantId) {
        const sellingPlanId = this.data.sellingPlanId ? Number(this.data.sellingPlanId) : null;
        return cart.items.find((item) => {
          const itemSellingPlanId = item.selling_plan_allocation?.selling_plan?.id || null;
          return (
            Number(item.variant_id) === Number(variantId) &&
            Number(itemSellingPlanId || 0) === Number(sellingPlanId || 0) &&
            this.sameProperties(item.properties, this.data.properties)
          );
        });
      }

      async addVariant(variantId, quantity) {
        const item = {
          id: Number(variantId),
          quantity,
          properties: this.normalizedProperties(this.data.properties),
        };
        if (this.data.sellingPlanId) item.selling_plan = Number(this.data.sellingPlanId);

        const response = await fetch(window.routes.cart_add_url, {
          ...fetchConfig(),
          body: JSON.stringify({ items: [item] }),
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || result.message || 'Unable to add this variant.');
        return result;
      }

      async changeLine(id, quantity, includeSections = false) {
        const body = { id, quantity };
        if (includeSections) {
          body.sections = ['cart-drawer', 'cart-icon-bubble'];
          body.sections_url = window.location.pathname;
        }
        const response = await fetch(window.routes.cart_change_url, {
          ...fetchConfig(),
          body: JSON.stringify(body),
        });
        const result = await response.json();
        if (!response.ok || result.errors || result.status) {
          throw new Error(result.description || result.message || result.errors || 'Unable to update the cart.');
        }
        return result;
      }

      async rollbackTarget(targetBefore, targetAfter) {
        if (!targetAfter) return;
        const originalQuantity = targetBefore?.quantity || 0;
        await this.changeLine(targetAfter.key, originalQuantity, false);
      }

      renderCart(cartState) {
        const drawer = document.querySelector('cart-drawer');
        if (drawer && cartState.sections && typeof drawer.renderContents === 'function') {
          drawer.renderContents(cartState, false);
          return;
        }
        document.dispatchEvent(
          new CustomEvent('cart-drawer:refresh', { bubbles: true, composed: true }),
        );
      }

      async waitForRenderedSelector(variantId) {
        const timeoutAt = Date.now() + 5000;

        while (Date.now() < timeoutAt) {
          const renderedSelector = Array.from(
            document.querySelectorAll('cart-drawer cart-variant-selector'),
          ).find(
            (selector) =>
              selector.isConnected &&
              Number(selector.dataset.currentVariantId) === Number(variantId),
          );

          if (renderedSelector) {
            await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
            return renderedSelector;
          }

          await new Promise((resolve) => setTimeout(resolve, 50));
        }

        throw new Error('The cart updated, but the refreshed item is not ready.');
      }

      async replaceVariant(newVariant) {
        const quantity = Number(this.dataset.quantity) || 1;
        const oldLineKey = this.dataset.lineKey;
        const cartBefore = await this.fetchCart();
        const targetBefore = this.targetLine(cartBefore, newVariant.id);

        await this.addVariant(newVariant.id, quantity);
        const cartAfterAdd = await this.fetchCart();
        const targetAfter = this.targetLine(cartAfterAdd, newVariant.id);

        try {
          const updatedCart = await this.changeLine(oldLineKey, 0, true);
          this.renderCart(updatedCart);
          return this.waitForRenderedSelector(newVariant.id);
        } catch (error) {
          try {
            await this.rollbackTarget(targetBefore, targetAfter);
          } catch (rollbackError) {
            console.error('Cart variant rollback failed:', rollbackError);
          }
          document.dispatchEvent(
            new CustomEvent('cart-drawer:refresh', { bubbles: true, composed: true }),
          );
          throw error;
        }
      }

      async onChange(event) {
        event.stopPropagation();
        if (this.isUpdating || cartVariantUpdateInProgress) return;
        const values = this.selectedValues();
        const variant = this.resolveVariant(event.target, values);
        if (variant?.available) this.syncSelections(variant);
        this.updateOptionAvailability();

        if (!variant) {
          this.restoreSelections();
          this.setStatus('Unavailable combination.', true);
          return;
        }
        if (!variant.available) {
          this.restoreSelections();
          this.setStatus('Sold out.', true);
          return;
        }
        if (Number(variant.id) === Number(this.dataset.currentVariantId)) {
          this.previousValues = values;
          this.setStatus('');
          return;
        }

        cartVariantUpdateInProgress = true;
        this.setBusy(true);
        this.setStatus('Updating…');
        try {
          const renderedSelector = await this.replaceVariant(variant);
          cartVariantUpdateInProgress = false;
          document.querySelectorAll('cart-drawer cart-variant-selector').forEach((selector) => {
            selector.setBusy(false);
          });
          renderedSelector.setStatus('');
        } catch (error) {
          console.error('Cart variant update failed:', error);
          cartVariantUpdateInProgress = false;
          if (this.isConnected) {
            this.restoreSelections();
            this.setStatus(error.message || 'Unable to update this item.', true);
            this.setBusy(false);
          } else {
            document.querySelectorAll('cart-drawer cart-variant-selector').forEach((selector) => {
              selector.setBusy(false);
            });
          }
        }
      }
    },
  );
}
